'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

const SHOW_DEV_BANNER = true;

// Fixed-width separator — same gap between every sentence, including loop boundary
const SEP = '        ✦        ';

const TICKER: Record<'en' | 'ru' | 'uk', string> = {
  en: [
    '🚧 Site is under active development — features may change without notice',
    '⚠️ Orders are NOT being processed until the official production launch',
    '🔧 Full store opens very soon — thank you for your patience!',
  ].join(SEP) + SEP,
  ru: [
    '🚧 Сайт в стадии разработки — функции могут меняться без предупреждения',
    '⚠️ Заказы НЕ обрабатываются до официального запуска в продакшн',
    '🔧 Полноценный магазин откроется совсем скоро — спасибо за терпение!',
  ].join(SEP) + SEP,
  uk: [
    '🚧 Сайт активно розробляється — функції можуть змінюватись без попередження',
    '⚠️ Замовлення НЕ обробляються до офіційного запуску в продакшн',
    '🔧 Повноцінний магазин відкриється дуже скоро — дякуємо за терпіння!',
  ].join(SEP) + SEP,
};

const COPIES = 4;
const SPEED = 60; // px/s

export function DevBanner() {
  const pathname = usePathname();
  const [lang, setLang] = useState<'en' | 'ru' | 'uk'>('en');
  const [reduced, setReduced] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(40);

  useEffect(() => {
    const detect = () => {
      try {
        const v = localStorage.getItem('lang');
        setLang(v === 'ru' ? 'ru' : v === 'uk' ? 'uk' : 'en');
      } catch {}
    };
    detect();
    window.addEventListener('lang-change', detect);
    return () => window.removeEventListener('lang-change', detect);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const h = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    if (!trackRef.current || reduced) return;
    const oneCopy = trackRef.current.scrollWidth / COPIES;
    if (oneCopy > 0) setDuration(oneCopy / SPEED);
  }, [lang, reduced]);

  // Hide on admin pages, style prototypes, and when disabled
  if (!SHOW_DEV_BANNER || pathname.startsWith('/admin') || pathname.startsWith('/labs') || pathname.startsWith('/delivery')) return null;

  const text = TICKER[lang];

  return (
    <>
      <style>{`
        @keyframes dev-run {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-100% / ${COPIES})); }
        }
        .dev-track {
          display: flex;
          width: max-content;
          will-change: transform;
          animation: dev-run var(--dur) linear infinite;
        }
        .dev-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .dev-track { animation: none; }
        }
      `}</style>

      <div
        role="status"
        aria-live="off"
        style={{
          width: '100%',
          overflow: 'hidden',
          background: 'linear-gradient(90deg,#7c2d12 0%,#c2410c 25%,#f97316 50%,#c2410c 75%,#7c2d12 100%)',
          borderBottom: '1px solid rgba(251,146,60,.28)',
          height: 34,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          flexShrink: 0,
          boxShadow: '0 2px 20px rgba(234,88,12,.3)',
        }}
      >
        <div style={{ position:'absolute',left:0,top:0,bottom:0,width:60,background:'linear-gradient(to right,#7c2d12 50%,transparent)',zIndex:2,pointerEvents:'none' }} />
        <div style={{ position:'absolute',right:0,top:0,bottom:0,width:60,background:'linear-gradient(to left,#7c2d12 50%,transparent)',zIndex:2,pointerEvents:'none' }} />

        {reduced ? (
          <span style={{
            color:'#fff',fontSize:12.5,fontFamily:'Inter,sans-serif',fontWeight:700,
            padding:'0 16px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',
            width:'100%',letterSpacing:'.015em',textShadow:'0 1px 3px rgba(0,0,0,.35)',
          }}>
            {text}
          </span>
        ) : (
          <div
            ref={trackRef}
            className="dev-track"
            style={{ '--dur': `${duration}s` } as React.CSSProperties}
          >
            {Array.from({ length: COPIES }).map((_, i) => (
              <span key={i} style={{
                color: '#fff',
                fontSize: 12.5,
                fontFamily: 'Inter,sans-serif',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                lineHeight: '34px',
                letterSpacing: '.02em',
                textShadow: '0 1px 5px rgba(0,0,0,.28)',
                flexShrink: 0,
              }}>
                {text}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
